import { Activity, CirclePause, GitMerge, Play, RotateCcw, Settings2 } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { landsWithOrgusLand, pipelineKey, queueEtas } from '@orgus/protocol';
import type { Id, Landing, LandingQueue, LandingState } from '@orgus/protocol';
import type { ReactNode } from 'react';
import { Button, ConfirmDialog, DropdownMenuLabel, EmptyState, ErrorState, GROUP_INDENT, LiveNumber, Pressable, ProjectMark, REVEAL_GROUP, RelativeTime, RevealSlot, RowStatus, Select, Skeleton, StatusPill, WorkStateIcon, cn, formatDuration, formatInt, plural, toast, useNow } from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { useOrgus, usePipelines } from '../../lib/store.ts';
import { projectConfig, retryLanding, setQueuePaused, useLanding, useMockLandingTicker } from './api.ts';
import { ConsentNotice } from './ConsentNotice.tsx';
import { LandingBoard } from './LandingBoard.tsx';
import { LandingDrawer, landingProblem } from './LandingDrawer.tsx';
import { QUEUE_STATE, landedToday, landingRowStatus, needsAttention, queueRunState, sortQueues } from './model.ts';
import type { QueueRunState } from './model.ts';
import { Notice } from './Notice.tsx';
import { CeilingHint } from '../trust/CeilingHint.tsx';
import { ceilingCommand } from '../trust/ceilingHint.ts';
import { SubmitDialog } from './SubmitDialog.tsx';
import { PipelinesTabs, TabMenu } from './PipelinesTabs.tsx';
import { Board } from '../pipelines/Board.tsx';

function QueueSkeleton() {
  return (
    <div className="flex flex-col gap-4" role="status" aria-label="Loading the landing queue">
      <Skeleton className="h-12 w-96" />
      <div className="flex items-start gap-3">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex min-w-48 flex-1 flex-col gap-2 rounded-card bg-sunken p-2">
            <Skeleton className="h-4 w-24" />
            {Array.from({ length: (i * 2 + 1) % 3 }, (_, j) => (
              <Skeleton key={j} className="h-24 rounded-card" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The picker value of a project that lands with orgus land: this prefix and its pipeline key. */
const SELF_QUEUE = 'orgus-land:';

/** One queue and no picker: its project's mark, its name and branch, and what it is in dim. */
function QueueName({ projectId, name, branch, kind }: { projectId: Id; name: string; branch: string; kind: string }) {
  return (
    <span className="flex min-w-0 items-center gap-2 text-ui">
      <ProjectMark projectId={projectId} name={name} size="xs" />
      <span className="min-w-0 truncate font-medium text-fg">
        {name} <span className="font-normal text-fg-muted">{kind}</span>
      </span>
      <span className="shrink-0 font-mono text-caption text-fg-subtle">{branch}</span>
    </span>
  );
}

/** "Orgus into main": a landing queue's name in the picker, the project and the branch it lands into, and the machine where two would read the same. */
export function queueOptionLabel(project: string, target: string, machine?: string): string {
  return `${project} into ${target}${machine ? ` on ${machine}` : ''}`;
}

/** The queue's state as the header's soft pill. */
const QUEUE_PILL: Record<QueueRunState, PillStatus> = { running: 'working', waiting: 'needs-you', paused: 'paused', idle: 'idle' };

/**
 * Parked and failed landings of this queue: what needs a person, as one group in the shared
 * look (its icon, label and count, the rows one step in). Each row: its status as a tinted icon,
 * the title over the fix, and the time; Retry and Resolve show on hover or focus (always under a
 * finger), and the row itself opens the landing.
 */
function Attention({ items, onOpen }: { items: Landing[]; onOpen: (id: Id) => void }) {
  const [busy, setBusy] = useState<Id | null>(null);
  const [error, setError] = useState<unknown>(null);
  if (!items.length) return null;
  const retry = async (l: Landing) => {
    setBusy(l.id);
    setError(null);
    try {
      await retryLanding(l.id);
      toast.success('Back in the queue', l.title);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section aria-labelledby="landing-needs-you" className="rounded-card border border-border bg-surface p-1 shadow-elevation-flat">
      <div className="flex h-row items-center gap-2 px-3">
        <WorkStateIcon state="needs-you" />
        <h2 id="landing-needs-you" className="text-ui font-medium text-fg">
          Needs you
        </h2>
        <LiveNumber value={items.length} className="text-ui text-fg-subtle" />
      </div>
      {error != null && (
        <div className="px-3 py-2">
          <ErrorState
            variant="inline"
            error={error}
            action={
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          />
        </div>
      )}
      <ul className={GROUP_INDENT}>
        {items.map((l) => (
          <li key={l.id} data-row="" className={cn(REVEAL_GROUP, 'flex min-h-row-2line items-center gap-3 rounded-control px-3 py-2 hover:bg-hover')}>
            <RowStatus {...landingRowStatus(l)} hideLabel focusable={false} />
            <Pressable onClick={() => onOpen(l.id)} className="flex min-w-0 flex-1 flex-col gap-1 rounded-control text-left">
              <span className="truncate text-ui font-medium text-fg max-sm:line-clamp-2 max-sm:whitespace-normal">{l.title}</span>
              {/* The cause in a few words; the steps to fix it are in the drawer. */}
              <span className="truncate text-caption text-fg-subtle" data-meta="" title={l.problem ? `${l.problem.what} ${l.problem.next}` : undefined}>
                {landingProblem(l)?.title ?? l.step}
              </span>
            </Pressable>
            {/* Retry on hover or focus, and always under a finger; the second action only where there is room. */}
            <RevealSlot className="gap-1 max-sm:hidden">
              {!l.landedSha && (
                <Button size="sm" variant="secondary" icon={<RotateCcw />} loading={busy === l.id} onClick={() => retry(l)}>
                  Retry
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onOpen(l.id)}>
                {l.status === 'parked' ? 'Resolve' : 'Open log'}
              </Button>
            </RevealSlot>
            {/* On a phone-wide screen there is no hover to reveal it: Retry stays in the row. */}
            {!l.landedSha && (
              <Button size="sm" variant="secondary" icon={<RotateCcw />} className="sm:hidden" loading={busy === l.id} onClick={() => retry(l)}>
                Retry
              </Button>
            )}
            <RelativeTime ts={l.endedAt ?? l.updatedAt} className="hidden w-16 shrink-0 text-right text-caption text-fg-subtle num sm:inline" />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The Landing queue tab: one board per repo x target branch, with Needs you above it. */
export function LandingQueueView() {
  const load = useLanding();
  useMockLandingTicker();
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [submit, setSubmit] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const now = useNow(5000);
  const s: LandingState | undefined = load.state;

  const name = useCallback((id: Id) => projects[id]?.name ?? 'Unknown project', [projects]);
  const machineName = useCallback((id: Id | null) => (id ? (machines[id]?.name ?? 'an unknown machine') : 'no machine yet'), [machines]);
  const queues = useMemo(() => (s ? sortQueues(s.queues, name) : []), [s, name]);
  const wanted = params.get('queue');
  // Projects that land with orgus land (their own pipeline says so) are queues too, beside the hub's.
  const pipelines = usePipelines();
  const selfLandings = useMemo(() => pipelines.filter((p) => landsWithOrgusLand(p) && projects[p.projectId]), [pipelines, projects]);
  const selfWanted = wanted?.startsWith(SELF_QUEUE) ? selfLandings.find((p) => pipelineKey(p) === wanted.slice(SELF_QUEUE.length)) : undefined;
  const queue = selfWanted ? undefined : (queues.find((q) => q.id === wanted) ?? queues[0]);
  const selfLanding = selfWanted ?? (queue ? undefined : selfLandings[0]);
  const landingId = params.get('landing');
  const sha = params.get('sha');
  const landing = s?.landings.find((l) => l.id === landingId || (sha && (l.headSha === sha || l.landedSha === sha || l.source.branch === `revert/${sha.slice(0, 8)}` || (l.source.branch && sha.startsWith(l.source.branch.replace(/^revert\//, ''))))));
  const queueLandings = useMemo(() => (s && queue ? s.landings.filter((l) => l.queueId === queue.id) : []), [s, queue]);
  const attention = useMemo(() => (queue ? needsAttention(queueLandings, now) : []), [queueLandings, queue, now]);
  const config = queue && s ? projectConfig(s, queue.projectId, projects[queue.projectId]?.defaultBranch ?? queue.target) : undefined;
  const saved = !!(queue && s?.configs.some((c) => c.projectId === queue.projectId));
  // Its queue: the changes not yet finished (submitted, waiting for CPU, checking).
  const waitingToLand = useMemo(() => {
    if (!selfLanding) return null;
    const items = selfLanding.items.filter((i) => i.state === 'queued' || i.state === 'active');
    return items.length ? { ...selfLanding, items, recent: [] } : null;
  }, [selfLanding]);
  const etas = useMemo(() => (queue ? queueEtas(queue, Object.fromEntries(queueLandings.map((l) => [l.id, l])), now) : {}), [queue, queueLandings, now]);

  const set = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null) n.delete(k);
            else n.set(k, v);
          }
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );

  const cmds = useMemo<Command[]>(() => {
    const out: Command[] = [{ id: 'landing.submit', title: 'Submit a change to the landing queue', group: 'Landing', icon: GitMerge, keywords: ['land', 'merge', 'queue', 'branch'], order: 1, perform: () => setSubmit(true) }];
    for (const q of queues) out.push({ id: `landing.open:${q.id}`, title: `Open the ${name(q.projectId)} ${q.target} landing queue`, group: 'Landing', icon: GitMerge, order: 10, perform: () => set({ queue: q.id, landing: null }) });
    if (queue) {
      out.push({
        id: `landing.${queue.paused ? 'resume' : 'pause'}:${queue.id}`,
        title: `${queue.paused ? 'Resume' : 'Pause'} the ${name(queue.projectId)} ${queue.target} landing queue`,
        group: 'Landing',
        icon: queue.paused ? Play : CirclePause,
        order: 20,
        perform: () => setConfirmPause(true),
      });
    }
    return out;
  }, [queues, queue, name, set]);
  useRegisterCommands(cmds, [cmds]);

  const pickerOptions = [
    // A landing queue is a project and the branch it lands into, so every option reads
    // "<project> into <branch>" (a pipeline, which runs on a machine, reads "<project> on <machine>").
    ...queues.map((q) => ({ value: q.id, label: queueOptionLabel(name(q.projectId), q.target), icon: <ProjectMark projectId={q.projectId} name={name(q.projectId)} size="xs" /> })),
    // A project landing with orgus land lands from one machine's checkout: where it would read the
    // same as a hub queue, the machine tells them apart ("Orgus into main on studio").
    ...selfLandings.map((p) => ({
      value: `${SELF_QUEUE}${pipelineKey(p)}`,
      label: queueOptionLabel(name(p.projectId), 'main', queues.some((q) => q.projectId === p.projectId && q.target === 'main') || selfLandings.some((o) => o !== p && o.projectId === p.projectId) ? (machines[p.machineId]?.name ?? 'an unknown machine') : undefined),
      icon: <ProjectMark projectId={p.projectId} name={name(p.projectId)} size="xs" />,
    })),
  ];
  const picker =
    pickerOptions.length > 1 ? (
      <Select
        size="sm"
        aria-label="Landing queue"
        value={queue ? queue.id : selfLanding ? `${SELF_QUEUE}${pipelineKey(selfLanding)}` : ''}
        onValueChange={(id) => set({ queue: id, landing: null })}
        options={pickerOptions}
        wrapperClassName="w-64 max-w-full max-sm:order-first max-sm:w-full"
      />
    ) : undefined;

  // The page's action sits in its one header row, where every Pipelines tab keeps its controls.
  const submitButton = (
    <Button variant="secondary" size="sm" icon={<GitMerge className="text-accent-text" />} onClick={() => setSubmit(true)}>
      Submit change
    </Button>
  );

  let body;
  // The view's controls, in the page's one header row beside the tabs.
  let header: ReactNode = null;
  if (load.phase === 'loading') body = <QueueSkeleton />;
  else if (load.phase === 'error' || !s) {
    body = (
      <div className="rounded-card border border-border bg-surface">
        <ErrorState error={load.error ?? { what: 'The landing queue did not load.', why: 'The hub did not send it.', next: 'Try again.' }} onRetry={load.retry} />
      </div>
    );
  } else if (!queue && selfLanding) {
    // A project landing with orgus land (ADR 0055): what agents submitted, waits for CPU and is
    // being checked, in the Pipelines rows (a row opens the change in Pipelines); else how today went.
    const items = waitingToLand?.items ?? [];
    const checking = items.filter((i) => i.state === 'active').length;
    const queued = items.length - checking;
    const today = landedToday(selfLanding, now);
    const count = today.count ? `${today.atLeast ? 'At least ' : ''}${plural(today.count, 'landing')} today` : 'No landings yet today';
    const openPipeline = () => navigate(`/pipelines?${new URLSearchParams({ pipeline: pipelineKey(selfLanding) }).toString()}`);
    // Why nothing moves (main is red): said above the queue, not left to a silent Queued.
    const held = !checking && queued > 0 ? selfLanding.hold : undefined;
    header = (
      <>
        <StatusPill status={held ? 'needs-you' : checking ? 'working' : queued ? 'queued' : 'idle'} label={held ? 'Paused' : checking ? 'Landing' : queued ? 'Queued' : 'Idle'} />
        {picker ?? <QueueName projectId={selfLanding.projectId} name={name(selfLanding.projectId)} branch="main" kind="lands with orgus land" />}
        <Button size="sm" variant="secondary" icon={<Activity />} onClick={openPipeline}>
          Open pipeline
        </Button>
        {submitButton}
        <TabMenu label="More landing queue controls" items={[{ label: 'Landing settings', icon: <Settings2 />, onSelect: () => navigate('/settings/landing-queue') }]} />
      </>
    );
    body = (
      <div className="flex flex-col gap-4">
        {held && <Notice title="Landing is paused.">{held}</Notice>}
        {waitingToLand ? (
          <section aria-label="Waiting to land" className="rounded-card border border-border bg-surface p-1 shadow-elevation-flat">
            <Board pipeline={waitingToLand} onOpen={(i) => navigate(`/pipelines?${new URLSearchParams({ pipeline: pipelineKey(selfLanding), item: i.id }).toString()}`)} />
          </section>
        ) : (
          <div className="rounded-card border border-border bg-surface">
            <EmptyState
              size="page"
              icon={<GitMerge />}
              title="Nothing waiting to land"
              description={`${count}. Branches submitted with orgus land --submit wait here.`}
            />
          </div>
        )}
      </div>
    );
  } else if (!queue) {
    body = (
      <div className="rounded-card border border-border bg-surface">
        <EmptyState
          size="page"
          icon={<GitMerge />}
          title="No landing queues yet"
          description="A queue appears when a branch is submitted to land."
          action={
            <>
              <Button variant="primary" icon={<GitMerge />} onClick={() => setSubmit(true)}>
                Submit change
              </Button>
              <Button variant="secondary" icon={<Settings2 />} onClick={() => navigate('/settings/landing-queue')}>
                Landing settings
              </Button>
            </>
          }
        />
      </div>
    );
  } else {
    const st = QUEUE_STATE[queueRunState(queue)];
    const settings = `/settings/landing-queue?project=${encodeURIComponent(queue.projectId)}`;
    header = (
      <>
        {queue.paused && queue.pausedBy && (
          <span className="flex shrink-0 items-baseline gap-1 text-caption text-fg-subtle">
            by {queue.pausedBy} <RelativeTime ts={queue.pausedAt ?? null} />
          </span>
        )}
        <StatusPill status={QUEUE_PILL[queueRunState(queue)]} label={st.label} />
        {picker ?? <QueueName projectId={queue.projectId} name={name(queue.projectId)} branch={queue.target} kind="landing queue" />}
        <Button size="sm" variant="secondary" icon={queue.paused ? <Play /> : <CirclePause />} onClick={() => setConfirmPause(true)}>
          {queue.paused ? 'Resume queue' : 'Pause queue'}
        </Button>
        {submitButton}
        <TabMenu
          label="More landing queue controls"
          header={config?.verifyCommand ? <DropdownMenuLabel className="whitespace-normal">Verifies with <span className="font-mono">{config.verifyCommand}</span></DropdownMenuLabel> : undefined}
          items={[{ label: 'Landing settings', icon: <Settings2 />, onSelect: () => navigate(settings) }]}
        />
      </>
    );
    body = (
      <div className="flex flex-col gap-4">
        {!saved || !config?.verifyCommand ? (
          <Notice
            title={`No verify command is set for ${name(queue.projectId)}.`}
            action={
              <Button size="sm" variant="secondary" onClick={() => navigate(`/settings/landing-queue?project=${encodeURIComponent(queue.projectId)}`)}>
                Set it
              </Button>
            }
          >
            The queue lands only what it has verified, so landings park until one is set.
          </Notice>
        ) : null}
        {queue.consent?.length ? <ConsentNotice queue={queue} projectName={name(queue.projectId)} /> : null}
        {queue.waiting && queue.order.length > 0 && !queue.consent?.length && (
          <Notice title="The next landing is waiting for a machine.">
            {queue.waiting}
            {ceilingCommand(queue.waiting) && <CeilingHint className="mt-2" command={ceilingCommand(queue.waiting)!} />}
          </Notice>
        )}
        <Attention items={attention} onOpen={(id) => set({ landing: id })} />
        <LandingBoard
          queue={queue}
          landings={queueLandings}
          onOpen={(id) => set({ landing: id })}
          selectedId={landingId}
          machineName={machineName}
          landedNote={`${formatInt(queue.landedCount)} landed${queue.avgMs != null ? `, typically ${formatDuration(queue.avgMs)}` : ''}`}
        />
      </div>
    );
  }

  return (
    <>
      <PipelinesTabs>{header}</PipelinesTabs>
      {body}
      <LandingDrawer
        landing={landing}
        queue={s?.queues.find((q) => q.id === landing?.queueId)}
        eta={landing ? (etas[landing.id] ?? null) : null}
        project={landing ? projects[landing.projectId] : undefined}
        verifyCommand={config?.verifyCommand ?? ''}
        machineName={machineName}
        onClose={() => set({ landing: null })}
      />
      <SubmitDialog
        open={submit}
        onOpenChange={setSubmit}
        projectId={queue?.projectId}
        target={queue?.target}
        onSubmitted={(l) => {
          toast.success('Added to the landing queue', `${l.title} lands on ${l.target} after the ones before it.`);
          set({ queue: l.queueId, landing: null });
        }}
      />
      <ConfirmDialog
        open={confirmPause && !!queue}
        onOpenChange={setConfirmPause}
        tone="primary"
        title={queue?.paused ? `Resume the ${queue ? name(queue.projectId) : ''} ${queue?.target ?? ''} queue?` : `Pause the ${queue ? name(queue.projectId) : ''} ${queue?.target ?? ''} queue?`}
        description={
          queue?.paused
            ? queue.order.length
              ? `The next of ${plural(queue.order.length, 'queued landing')} starts right away.`
              : 'It picks up new landings as they arrive.'
            : `Nothing new starts until you resume it.${queue?.activeId ? ' The landing in progress finishes first.' : ''}${queue?.order.length ? ` ${plural(queue.order.length, 'queued landing')} keep${queue.order.length === 1 ? 's' : ''} its place.` : ''}`
        }
        confirmLabel={queue?.paused ? 'Resume queue' : 'Pause queue'}
        onConfirm={async () => {
          if (!queue) return;
          await setQueuePaused(queue.id, !queue.paused);
          toast.success(queue.paused ? 'Queue resumed' : 'Queue paused', `${name(queue.projectId)} ${queue.target}`);
        }}
      />
    </>
  );
}
